import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Gift, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClaimGift() {
  const [searchParams] = useSearchParams();
  const claimToken = searchParams.get('token');
  const navigate = useNavigate();
  const { user, token, fetchUser } = useAuth();

  const [status, setStatus] = useState('idle'); // idle, claiming, success, error
  const [message, setMessage] = useState('');
  const [credits, setCredits] = useState(0);
  const [newBalance, setNewBalance] = useState(0);

  const handleClaim = async () => {
    if (!claimToken || !token) return;
    setStatus('claiming');
    try {
      const res = await axios.post(
        `${API}/gifts/claim?claim_token=${encodeURIComponent(claimToken)}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCredits(res.data.credits);
      setNewBalance(res.data.new_balance);
      setMessage(res.data.message);
      setStatus('success');
      if (fetchUser) fetchUser();
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to claim gift. Please try again.');
      setStatus('error');
    }
  };

  useEffect(() => {
    if (!claimToken) {
      setStatus('error');
      setMessage('No gift token found. Please use the link from your email.');
      return;
    }
    // Auto-claim once the user is authenticated. This handles both the case where
    // they were already signed in AND the case where they just completed the OAuth
    // round-trip (AuthCallback restores them here via localStorage redirect).
    if (user && token && status === 'idle') {
      handleClaim();
    }
    // eslint-disable-next-line
  }, [user, token, claimToken]);

  if (!user) {
    return (
      <div className="container mx-auto px-4 sm:px-6 py-16 text-center">
        <div className="max-w-md mx-auto">
          <div className="w-20 h-20 rounded-full bg-[#FFB238]/10 flex items-center justify-center mx-auto mb-6">
            <Gift className="w-10 h-10 text-[#FFB238]" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4">Claim Your Gift</h1>
          <p className="text-gray-400 mb-8">You need to sign in first to claim your gift credits.</p>
          <button
            onClick={() => {
              // Stash the claim URL so AuthCallback redirects here after sign-in.
              // Plain navigate() loses the `?token=` through OAuth round-trip.
              try {
                localStorage.setItem('post_login_redirect', `/claim-gift?token=${claimToken}`);
              } catch (_) {}
              const returnUrl = `${window.location.origin}/auth/callback`;
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(returnUrl)}`;
            }}
            className="px-8 py-4 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold text-lg hover:bg-[#FFDA82] transition-colors"
            data-testid="claim-sign-in-button"
          >
            Sign In to Claim
          </button>
          <p className="text-gray-500 text-sm mt-4">Don't have an account? Sign up with the email this gift was sent to.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 py-16 text-center">
      <div className="max-w-md mx-auto">
        {status === 'idle' && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#FFB238]/10 flex items-center justify-center mx-auto mb-6">
              <Gift className="w-10 h-10 text-[#FFB238]" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4">Claim Your Gift</h1>
            <p className="text-gray-400 mb-8">You have a gift waiting! Click below to add the credits to your account.</p>
            <button
              onClick={handleClaim}
              className="px-8 py-4 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold text-lg hover:bg-[#FFDA82] transition-colors"
              data-testid="claim-gift-button"
            >
              Claim Gift Credits
            </button>
          </>
        )}

        {status === 'claiming' && (
          <>
            <Loader2 className="w-16 h-16 text-[#FFB238] animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-white mb-4">Adding credits...</h1>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4">{message}</h1>
            <div className="bg-[#1A2035] rounded-xl border border-[#222D4A] p-6 mb-8">
              <p className="text-gray-400 text-sm mb-2">Credits Added</p>
              <p className="text-4xl font-bold text-[#FFB238]">{credits.toLocaleString()}</p>
              <p className="text-gray-500 text-sm mt-3">New balance: <span className="text-white font-medium">{newBalance.toLocaleString()} credits</span></p>
            </div>
            <button
              onClick={() => navigate('/')}
              className="px-8 py-4 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold text-lg hover:bg-[#FFDA82] transition-colors"
              data-testid="start-creating-button"
            >
              Start Creating
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-4">Couldn't Claim Gift</h1>
            <p className="text-gray-400 mb-8">{message}</p>
            <button
              onClick={() => navigate('/')}
              className="px-8 py-3 rounded-lg bg-[#222D4A] text-white font-medium hover:bg-[#2A3550] transition-colors"
            >
              Go to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
}
